import * as React from 'react';
import { FiSearch } from 'react-icons/fi';
import { COLUMNS_START_PADDING, HEADER_HEIGHT, SIDEBAR_WIDTH } from '../../layout';
import { scrollTaskIntoView, selectTask } from '../../state';
import { useDateRange, useExpandedColumns, useTaskCategories, useGroupBy, useReleases, useTasksByRelease, useFilters } from '../../state/context';
import { Filter, Task, TaskCategory } from '../../types';
import ColumnGrid from '../ColumnGrid';
import AuthButton from './AuthButton';
import HeaderCheckbox from './Checkbox';
import DatePanel from './DatePanel';
import HeaderColumn from './HeaderColumn';
import SearchPanel from './SearchPanel';
import Switch from './Switch';
import { dispatchToggleTasksPanel } from '../../state/actions';
import classNames from 'classnames';

const getNumberOfSolutions = (tasks: Task[]) => {
  const solutionIds: number[] = [];

  tasks.forEach((task) => {
    if (!solutionIds.includes(task.solutionId)) {
      solutionIds.push(task.solutionId);
    }
  });

  return solutionIds.length;
};

const Header = () => {
  const releases = useReleases();
  const [filters, setFilters] = useFilters();
  const [expandedColumns, toggleColumn] = useExpandedColumns();
  const [isSearchPanelOpen, setIsSearchPanelOpen] = React.useState(false);
  const [groupBy, setGroupBy] = useGroupBy();
  const [dateRange, setDateRange] = useDateRange();
  const tasksByRelease = useTasksByRelease();
  const innerRef = React.useRef<HTMLDivElement>(null);

  const toggleFilter = (filter: Filter) => {
    if (filters.includes(filter)) {
      setFilters(filters.filter((f) => f !== filter));
    } else {
      setFilters([...filters, filter]);
    }
  }

  React.useEffect(() => {
    const handleScroll = () => {
      if (innerRef.current) {
        innerRef.current.style.transform = `translateX(-${window.scrollX}px)`;
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <div
      className="bg-white shadow-lg fixed top-0 left-0 right-0 z-20"
      style={{ height: `${HEADER_HEIGHT}px` }}
    >
      <div
        className="shadow-lg h-full px-5 py-3.5 text-blue-800 relative flex flex-col"
        style={{ width: `${SIDEBAR_WIDTH}px` }}
      >
        <div className="flex items-center justify-between">
          <h1 className="text-ms font-bold uppercase mb-1">FIU Roadmap</h1>
          <AuthButton />
        </div>
        <div className="flex flex-wrap mt-3">
          <HeaderCheckbox
            checked={filters.includes('error')}
            type="error"
            label="Fejl"
            className="mr-5 mb-1.5"
            onChange={() => toggleFilter('error')}
          />
          <HeaderCheckbox
            checked={filters.includes('request')}
            type="request"
            label="Ønske"
            className="mr-5 mb-1.5"
            onChange={() => toggleFilter('request')}
          />
          <HeaderCheckbox
            checked={filters.includes('support')}
            type="support"
            label="Support"
            className="mr-5 mb-1.5"
            onChange={() => toggleFilter('support')}
          />
          <HeaderCheckbox
            checked={filters.includes('maintenance')}
            type="maintenance"
            label="Vedligehold"
            className="mr-5"
            onChange={() => toggleFilter('maintenance')}
          />
          <HeaderCheckbox
            checked={filters.includes('documentation')}
            type="documentation"
            label="Manual"
            onChange={() => toggleFilter('documentation')}
          />
        </div>
        <div className="text-xxs flex items-center justify-between mt-auto">
          <DatePanel
            dateRange={dateRange}
            onChange={setDateRange}
            onToggleChange={(isDatePanelOpen) => {
              if (isDatePanelOpen) {
                setIsSearchPanelOpen(false);
              }
            }}
          />
          <div className="flex-none h-6 border-l border-gray-300"></div>
          <label
            className="flex items-center border-left h-5 leading-4 tracking-wide cursor-pointer"
          >
            <Switch
              checked={groupBy === 'organization'}
              onChange={(checked) => setGroupBy(checked ? 'organization' : 'solution')}
            />
            <span className="font-bold uppercase ml-2">Forbund</span>
          </label>
          <div className="flex-none h-6 border-l border-gray-300"></div>
          <button
            type="button"
            onClick={(event) => {
              event.preventDefault();
              if (isSearchPanelOpen) {
                selectTask(null);
              }
              setIsSearchPanelOpen(!isSearchPanelOpen);

            }}
            className={classNames(
              'h-6 w-6 flex items-center justify-center rounded-md',
              isSearchPanelOpen && 'bg-gray-200',
            )}
          >
            <FiSearch className="w-4 h-4" />
          </button>
        </div>
        <SearchPanel
          isOpen={isSearchPanelOpen}
          onClose={() => {
            setIsSearchPanelOpen(false);
            selectTask(null);
          }}
          onSelect={(task) => {
            if (!task.releaseId) {
              dispatchToggleTasksPanel(true);
            }

            scrollTaskIntoView(task.id);
          }}
        />
      </div>
      <div
        className="absolute top-0 bottom-0 right-0 overflow-hidden"
        style={{
          left: `${SIDEBAR_WIDTH}px`,
          paddingLeft: COLUMNS_START_PADDING,
        }}
      >
        <div className="flex h-full relative" ref={innerRef}>
          <ColumnGrid excludeLeftPadding={true} />
          {releases.map((release) => (
            <HeaderColumn
              key={release.id}
              release={release}
              isExpanded={expandedColumns.includes(release.id)}
              numberOfTasks={tasksByRelease[release.id]?.length || 0}
              numberOfSolutions={getNumberOfSolutions(tasksByRelease[release.id] || [])}
              toggle={() => toggleColumn(release.id)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default Header;
